<template>
  <Dialog :common-dialog="dialog" :dialog-width="dialogWidth">
    <template v-slot:title>
      <div class="d-flex align-center justify-space-between w-100 px-2 py-1">
        <div class="d-flex align-center">
          <v-avatar size="38" color="cyan lighten-5" class="mr-3">
            <v-icon color="cyan darken-2">mdi-map-marker-outline</v-icon>
          </v-avatar>
          <div class="dialog-title color-custom-blue font-weight-700">
            Select a {{ label }}
          </div>
        </div>
        <v-btn
          icon
          small
          class="custom-grey-border bg-slate-50"
          :disabled="loading"
          v-on:click="$emit('close', true)"
        >
          <v-icon color="slate-600" small>mdi-close</v-icon>
        </v-btn>
      </div>
    </template>
    <template v-slot:body>
      <div class="px-4 w-100">
        <!-- SEARCH & ACTION TOOLBAR -->
        <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-2 mb-3">
          <label
            for="address-search"
            class="font-size-13 font-weight-600 mb-1 d-block text-slate-700"
          >
            Which <span class="text-lowercase">{{ label }}</span> would you like
            to select this for?
          </label>
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="6">
              <v-text-field
                id="address-search"
                v-model="search"
                :placeholder="`Search ${label}...`"
                dense
                filled
                solo
                flat
                hide-details
                color="cyan"
                prepend-inner-icon="mdi-magnify"
                :loading="loading"
                :disabled="loading"
                class="custom-erp-input bg-white rounded-lg"
                @keydown.enter="getAddress()"
              />
            </v-col>
            <v-col cols="12" sm="6" md="6" class="d-flex align-center gap-2">
              <v-btn
                v-on:click="getAddress()"
                :loading="loading"
                :disabled="loading"
                depressed
                color="cyan darken-2 white--text"
                class="custom-bold-button mr-2"
                height="36"
              >
                <v-icon small left>mdi-magnify</v-icon>
                Search
              </v-btn>

              <v-btn
                depressed
                v-if="
                  getPermission('property:create') &&
                  (label != 'Billing Location' || isQuotation)
                "
                color="custom-btn-color white--text"
                class="custom-bold-button"
                height="36"
                v-on:click="createPropertyDialog = true"
              >
                <v-icon small left>mdi-plus</v-icon>
                Create Location
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <!-- ADDRESS LIST AREA -->
        <perfect-scrollbar
          :options="{ suppressScrollX: true }"
          class="scroll pr-1"
          style="max-height: calc(100vh - 340px); position: relative"
        >
          <div v-if="list.length">
            <v-card
              v-for="(row, index) in list"
              :key="`address-list-key-${index}`"
              flat
              outlined
              class="rounded-lg px-3 py-2 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
              v-on:click="selectAddress(row)"
            >
              <v-row dense align="center">
                <!-- Left: Address Details -->
                <v-col cols="12" md="8" class="py-1">
                  <div class="d-flex align-center flex-wrap gap-2 mb-1">
                    <Barcode :barcode="row.barcode" />
                    <v-chip
                      label
                      small
                      class="ml-1"
                      outlined
                      v-if="row.is_same == 1"
                      style="min-height: 22px; font-size: 12px"
                      color="orange"
                    >
                      Site Location &amp; Billing
                    </v-chip>
                    <v-chip
                      v-else
                      label
                      small
                      class="ml-1"
                      :color="row.type == 1 ? 'cyan' : 'orange'"
                      outlined
                      style="min-height: 22px; font-size: 12px"
                    >
                      <template v-if="row.type == 1">Site Location</template>
                      <template v-else>Billing</template>
                    </v-chip>
                  </div>

                  <div
                    class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                  >
                    <v-icon small left color="slate-600"
                      >mdi-map-marker-outline</v-icon
                    >
                    <span>
                      <span v-if="row?.property_name"
                        >{{ row.property_name }} - </span
                      >{{ row.property_address }}
                    </span>
                  </div>
                </v-col>

                <!-- Right: Select CTA -->
                <v-col
                  cols="12"
                  md="4"
                  class="text-md-right d-flex align-center justify-md-end py-1"
                >
                  <v-btn
                    small
                    depressed
                    color="cyan lighten-5"
                    class="custom-bold-button px-3 cyan--text text-darken-2"
                  >
                    Select Location
                    <v-icon small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>

          <!-- EMPTY STATE -->
          <div
            v-else
            class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
          >
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon color="slate-400" large
                >mdi-map-marker-off-outline</v-icon
              >
            </v-avatar>
            <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
              No {{ label || "Location" }}s Found
            </div>
            <div class="caption-1 text-muted max-w-300 mb-3">
              There are no matching
              {{
                lodash.isEmpty(label) == false
                  ? label.toLowerCase()
                  : "location"
              }}
              records in the database.
            </div>
            <v-btn
              depressed
              v-if="
                getPermission('property:create') &&
                (label != 'Billing Location' || isQuotation)
              "
              color="custom-btn-color white--text"
              class="custom-bold-button"
              height="32"
              v-on:click="createPropertyDialog = true"
            >
              <v-icon small left>mdi-plus</v-icon>
              Create Location
            </v-btn>
          </div>
        </perfect-scrollbar>
      </div>
      <Dialog
        v-if="createPropertyDialog"
        :commonDialog="createPropertyDialog"
        :dialogWidth="768"
      >
        <template v-slot:title>
          Create
          {{
            label === "Billing Location" ? "Billing Location" : "Site Location"
          }}
        </template>
        <template v-slot:body>
          <v-form
            ref="createProperty"
            v-model.trim="formValid"
            lazy-validation
            v-on:submit.stop.prevent="createProperty"
          >
            <DialogPropertyAddress
              :zones="zones"
              :isDialog="true"
              isCustomerFrom
              v-on:savePropertyAddress="updateProperty"
            >
            </DialogPropertyAddress>
          </v-form>
        </template>
        <template v-slot:action>
          <v-btn
            depressed
            :disabled="!formValid || formLoading"
            :loading="formLoading"
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            v-on:click="createProperty"
            >Save
          </v-btn>
          <v-btn
            depressed
            :disabled="formLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            v-on:click="createPropertyDialog = false"
            >Close
          </v-btn>
        </template>
      </Dialog>
    </template>
  </Dialog>
</template>

<script>
import { mapGetters } from "vuex";
import Dialog from "@/view/pages/partials/Dialog.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";
import { QUERY, PUT, CLEAR_ERROR } from "@/core/services/store/request.module";
import DialogPropertyAddress from "@/view/pages/customer/create-or-update/Property-Address.vue";
import { SET_PROPERTY, SET_BILLING } from "@/core/services/store/visit.module";
import ObjectPath from "object-path";

export default {
  name: "select-address",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: null,
    },
    zones: {
      type: Array,
      default: () => [],
      required: false,
    },
    label: {
      type: String,
      default: null,
    },
    isQuotation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      createPropertyDialog: false,
      search: null,
      formValid: true,
      formLoading: false,
      list: [],
      propertyAddress: {
        type: 1,
        is_same: 0,
        is_tenant: 0,
        unit_no: null,
        contact_person: null,
        property_name: null,
        street_1: null,
        doUpdate: 0,
        street_2: null,
        country: "singapore",
        zip_code: null,
        latitude: null,
        longitude: null,
        remarks: null,
        zone_territory: null,
        ptw_required: 0,
      },
    };
  },
  watch: {
    dialog(param) {
      this.search = null;
      if (param) {
        this.getAddress();
      }
    },
  },
  components: {
    Dialog,
    DialogPropertyAddress,
    Barcode,
  },
  methods: {
    selectAddress(row) {
      if (this.type == "billing") {
        this.$store.dispatch(SET_BILLING, row);
      }

      if (this.type == "property") {
        this.$store.dispatch(SET_PROPERTY, row);
      }

      this.$emit("close", true);
      this.loading = false;
      this.search = null;
      this.list = [];
    },
    updateProperty(param) {
      this.propertyAddress = param;
    },
    createProperty() {
      const _this = this;
      if (!_this.$refs.createProperty.validate()) {
        return false;
      }
      let addressFormData = new Array();
      addressFormData.push(_this.propertyAddress);
      const formData = _this.lodash.pickBy(
        addressFormData,
        _this.lodash.identity
      );

      if (_this.type === "billing") {
        _this.propertyAddress.type = 2;
      } else {
        _this.propertyAddress.type = 1;
      }
      if (_this.lodash.isEmpty(formData) === false) {
        _this.formLoading = true;
        _this.$store.dispatch(CLEAR_ERROR, {});
        _this.$store
          .dispatch(PUT, {
            url: "address/" + _this.vCustomerId,
            data: { address: formData },
          })
          .then(() => {
            /*  _this.searchCustomerProperty(); */
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.formLoading = false;
            this.createPropertyDialog = false;
            this.getAddress();
          });
      }
    },
    getAddress() {
      this.loading = true;
      this.list = [];
      this.$store
        .dispatch(QUERY, {
          url: `property-list/${this.vCustomerId}`,
          data: { search: this.search },
        })
        .then((response) => {
          this.list = ObjectPath.get(response, "data", []);
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  computed: {
    ...mapGetters(["vCustomerId"]),
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 40);
    },
  },
};
</script>
